export const SCOPE_LEVELS = ['regions', 'counties', 'markets', 'departments'];

export const EMPTY_SCOPE_FILTERS = {
  regions: [],
  counties: [],
  markets: [],
  departments: [],
};

const uniqueSorted = (values) =>
  Array.from(new Set(values.filter((value) => value != null && value !== ''))).sort((a, b) =>
    a.localeCompare(b),
  );

export const isGuideDepartment = (department) =>
  /^guide\b/i.test(`${department?.value ?? department?.label ?? ''}`.trim());

const matchesGeoFilters = (department, filters) =>
  (!filters.regions.length || filters.regions.includes(department.regionName)) &&
  (!filters.counties.length || filters.counties.includes(department.countyName)) &&
  (!filters.markets.length || filters.markets.includes(department.marketName)) &&
  (!filters.departments.length || filters.departments.includes(department.id));

export const getRegionOptions = (departments) =>
  uniqueSorted(departments.filter((d) => !isGuideDepartment(d)).map((d) => d.regionName));

export const getCountyOptions = (departments, filters) =>
  uniqueSorted(
    departments
      .filter((d) => !isGuideDepartment(d))
      .filter((d) => !filters.regions.length || filters.regions.includes(d.regionName))
      .map((d) => d.countyName),
  );

export const getMarketOptions = (departments, filters) =>
  uniqueSorted(
    departments
      .filter((d) => !isGuideDepartment(d))
      .filter(
        (d) =>
          (!filters.regions.length || filters.regions.includes(d.regionName)) &&
          (!filters.counties.length || filters.counties.includes(d.countyName)),
      )
      .map((d) => d.marketName),
  );

export const getDepartmentOptions = (departments, filters) =>
  departments
    .filter((d) => !isGuideDepartment(d))
    .filter(
      (d) =>
        (!filters.regions.length || filters.regions.includes(d.regionName)) &&
        (!filters.counties.length || filters.counties.includes(d.countyName)) &&
        (!filters.markets.length || filters.markets.includes(d.marketName)),
    );

export const pruneScopeFilters = (departments, filters) => {
  const validCounties = getCountyOptions(departments, { ...filters, counties: [] });
  const counties = filters.counties.filter((county) => validCounties.includes(county));

  const validMarkets = getMarketOptions(departments, { ...filters, counties, markets: [] });
  const markets = filters.markets.filter((market) => validMarkets.includes(market));

  const validDepartments = getDepartmentOptions(departments, {
    ...filters,
    counties,
    markets,
    departments: [],
  }).map((d) => d.id);
  const departmentIds = filters.departments.filter((id) => validDepartments.includes(id));

  return { regions: filters.regions, counties, markets, departments: departmentIds };
};

export const departmentsInScope = (departments, filters, guideEnabled) => {
  const scoped = departments.filter(
    (department) => !isGuideDepartment(department) && matchesGeoFilters(department, filters),
  );

  if (!guideEnabled) return scoped;

  return [...scoped, ...departments.filter(isGuideDepartment)];
};

export const hasActiveScopeFilters = (filters) =>
  SCOPE_LEVELS.some((level) => filters[level].length > 0);

export const toggleScopeValue = (filters, level, value) => {
  const current = filters[level];
  const next = current.includes(value)
    ? current.filter((existing) => existing !== value)
    : [...current, value];
  return { ...filters, [level]: next };
};

const sameValues = (a = [], b = []) =>
  a.length === b.length && [...a].sort().every((value, index) => value === [...b].sort()[index]);

export const filtersEqualToPinnedScope = (filters, guideEnabled, pinnedScope) => {
  if (!pinnedScope) return false;
  return (
    Boolean(guideEnabled) === Boolean(pinnedScope.guideEnabled) &&
    SCOPE_LEVELS.every((level) => sameValues(filters[level], pinnedScope[level]))
  );
};

export const scopeToPinnedPayload = (filters, guideEnabled) => ({
  regions: filters.regions,
  counties: filters.counties,
  markets: filters.markets,
  departments: filters.departments,
  guideEnabled: Boolean(guideEnabled),
});
